import { PageMap } from '@wix/wixstores-client-core';
import { MinicartActions } from './actions/minicartActions';
import { TinyCartActions } from './actions/tinyCartActions';
import { SideCartActions } from './actions/sideCartActions';
import {
  CatalogReference,
  SuccessPopupActions,
} from './actions/successPopupActions';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';
import { getLeanGQLCartFromCartV1 } from '../../services/current-cart-service/getLeanGQLCartFromCartV1';
import { loadMiniCartIfNotLoaded } from './cartPublicApi.utils';
import { intToGuid } from '../../utils/utils';
import { EcomPlatformViewerScriptContext } from '../../viewer-script/types';
import type { CartMethodOptions } from '../types';
import { SPECS } from '../../constants';
import { BIService } from '../../services/BIService';
import { AnalyticsService } from '../../services/AnalyticsService';

export function createCartPublicApi({
  context,
  origin,
}: {
  context: EcomPlatformViewerScriptContext;
  origin: string;
}) {
  const getBIService = () =>
    new BIService({ siteStore: context.siteStore, origin });
  const getAnalyticsService = () =>
    new AnalyticsService({ siteStore: context.siteStore });
  const getMiniCartActions = (siteStore: SiteStore) =>
    new MinicartActions({ siteStore, origin });
  const getSideCartActions = (siteStore: SiteStore) =>
    new SideCartActions({ siteStore, origin });
  const getTinyCartActions = (siteStore: SiteStore) =>
    new TinyCartActions({ siteStore, origin, context });
  const getSuccessPopupActions = (siteStore: SiteStore) =>
    new SuccessPopupActions({ siteStore, origin });
  const hideMiniCart = () => {
    if (context.siteStore.isMobile()) {
      throw Error("can't handle mini cart in mobile");
    }

    getMiniCartActions(context.siteStore).hideMinicart();
  };

  const openSideCartOrMiniCart = async (
    options: { allowShowingSideCartOnMobile: boolean } = {
      allowShowingSideCartOnMobile: false,
    },
  ) => {
    const sideCartActions = getSideCartActions(context.siteStore);
    return sideCartActions.showSideCart(options);
  };

  return {
    getCurrentCart: async () => {
      return getLeanGQLCartFromCartV1(context.currentCartService);
    },
    onChange: (listener: any): void => {
      return context.currentCartService.onChange(listener);
    },
    removeProduct: async (
      cartItemId: string,
      _options: CartMethodOptions = { silent: false },
    ): Promise<void> => {
      const lineItemId = intToGuid(Number(cartItemId));
      const previousCart = await context.currentCartService.getCurrentCart();
      const cart =
        await context.currentCartService.removeLineItemsFromCurrentCart({
          lineItemIds: [lineItemId],
        });
      void getBIService().removeLineItem({ cart, origin });
      void getAnalyticsService().trackRemoveLineItem({
        lineItemId,
        updatedCart: cart,
        previousCart,
      });
    },
    applyCoupon: async (couponCode: string): Promise<void> => {
      const cart = await context.currentCartService.addCouponToCurrentCart({
        coupon: { code: couponCode },
      });
      void getBIService().addCoupon({ cart, couponCode, origin });
    },
    removeCoupon: async (): Promise<void> => {
      const previousCart = await context.currentCartService.getCurrentCart();
      const couponId = previousCart.coupons?.[0]?._id;
      const couponCode = previousCart.coupons?.[0]?.code;
      if (!couponId) {
        return;
      }
      const cart = await context.currentCartService.removeCouponFromCurrentCart(
        {
          couponId,
        },
      );
      void getBIService().removeCoupon({ couponCode, origin, cart });
    },
    updateLineItemQuantity: async (
      cartItemId: number,
      quantity: number,
      _options: CartMethodOptions = { silent: false },
    ): Promise<void> => {
      const lineItemId = intToGuid(cartItemId);
      const cart =
        await context.currentCartService.updateLineItemsInCurrentCart({
          lineItems: [{ lineItemId, quantity: { newQuantity: quantity } }],
        });
      void getBIService().updateLineItemQuantity({ origin, cart });
    },
    showMinicart: async () => openSideCartOrMiniCart(),
    hideMinicart: hideMiniCart,
    openSideCart: (
      options: { allowShowingSideCartOnMobile: boolean } = {
        allowShowingSideCartOnMobile: false,
      },
    ) => openSideCartOrMiniCart(options),
    showTinycart: async (options?: {
      addedProductId?: string;
    }): Promise<void> => {
      if (context.siteStore.isMobile()) {
        throw Error("can't open success popup in mobile");
      }
      await loadMiniCartIfNotLoaded(context);
      return getTinyCartActions(context.siteStore).showTinyCart(options);
    },
    hideTinycart: (): void => {
      if (context.siteStore.isMobile()) {
        throw Error("can't open success popup in mobile");
      }

      getTinyCartActions(context.siteStore).hideTinyCart();
    },
    reloadCart: (): Promise<void> => {
      return context.currentCartService.invalidateAndReload();
    },
    hasSideCart: () => getSideCartActions(context.siteStore).hasSideCart(),
    hasSuccessPopup: () =>
      getSuccessPopupActions(context.siteStore).hasSuccessPopup(),
    hasCartIcon: () => Promise.resolve(context.siteStore.isCartIconExists),
    openSuccessPopup: async (
      catalogReference: CatalogReference,
    ): Promise<void> => {
      if (!context.siteStore.experiments.enabled(SPECS.OpenSuccessPopup)) {
        return openSideCartOrMiniCart();
      }
      try {
        return await getSuccessPopupActions(context.siteStore).showSuccessPopup(
          catalogReference,
        );
      } catch {
        return openSideCartOrMiniCart();
      }
    },
    preloadTinyCart: async () => {
      return loadMiniCartIfNotLoaded(context);
    },
    preloadSideCart: async () => {
      const sideCartActions = getSideCartActions(context.siteStore);
      const sideCartName = await sideCartActions.getSideCartName();
      if (!sideCartName) {
        return;
      }

      context.siteStore.siteApis.prefetchPageResources({
        lightboxes: [sideCartName],
      });
    },
    preloadCartPage: async () => {
      try {
        const sectionUrl = await context.siteStore.getSectionUrl(PageMap.CART);
        if (sectionUrl.relativeUrl) {
          context.siteStore.siteApis.prefetchPageResources({
            pages: [sectionUrl.relativeUrl],
          });
        }
      } catch (e) {
        console.error('failed to prefetch cart page', e);
      }
    },
  };
}
